import React from 'react';
import {unwrapResult} from '@reduxjs/toolkit';
import {useDispatch, useSelector} from 'react-redux';
import {UiQuestionnaireLayout, Col, UiText, UiDivider, UiButton, Row, UiSimpleGrid} from '~/components/elements';
import {METHOD_KEYS, FERTILITY_TEST_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {addQuestionnaire, bookAppointment} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {redirectInIframe} from '~/utils/helpers';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire6Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === FERTILITY_TEST_KEYS.YOUR_TEST_PACKAGE);
  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);
  const {wpAuthUser} = medicalQuestionnaire;
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[FERTILITY_TEST_KEYS.YOUR_TEST_PACKAGE]?.data,
  );
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const currentAptRefAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[FERTILITY_TEST_KEYS.APPOINTMENT_PREFERENCES]?.data,
  );
  const {loading} = useRequestState(addQuestionnaire);
  const {loading: loadingAppointment} = useRequestState(bookAppointment);
  const {inputs, setInputs} = useInput({
    productId: currentAnswer?.productId || questionnaire?.packages?.[0]?.productId || '',
  });

  const onClickSubmit = async () => {
    const questionnaireAnswer = {
      key: FERTILITY_TEST_KEYS.YOUR_TEST_PACKAGE,
      index: questionnaire?.index || '',
      data: inputs,
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    let currentDataAptRef = currentAptRefAnswer;
    if (currentAptRefAnswer?.selectedOption) {
      const {selectedOption, ...rest} = currentAptRefAnswer;
      currentDataAptRef = rest;
    }

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [FERTILITY_TEST_KEYS.YOUR_TEST_PACKAGE]: {...questionnaireAnswer},
            [FERTILITY_TEST_KEYS.APPOINTMENT_PREFERENCES]: {
              ...medicalQuestionnaireAnswer[FERTILITY_TEST_KEYS.APPOINTMENT_PREFERENCES],
              data: currentDataAptRef,
            },
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.FERTILITY_TEST,
        },
      }),
    );
    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      const appointmentAction = await dispatch(
        bookAppointment({
          data: {startTime: null, userQuestionnaireId: result?.medical?.id},
          platform: isWebview ? 1 : 2,
        }),
      );
      if (requestOK(appointmentAction, bookAppointment)) {
        const bookResult = unwrapResult(appointmentAction);
        if (bookResult?.checkoutURL) {
          redirectInIframe(bookResult?.checkoutURL, '_self');
        }
      }
    }
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={!inputs?.productId}
      loading={loading || loadingAppointment}
    >
      <Col align="center" textAlign="center">
        <UiText variant="mdqTitle" my={10}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiSimpleGrid minChildWidth={['100%', '345px']} spacing="20px">
          {questionnaire?.packages?.map(value => (
            <Col
              key={value?.label}
              bg="white"
              style={{
                boxShadow: '0px 6px 25px rgba(0, 0, 0, 0.1)',
                borderRadius: '16px',
                cursor: 'pointer',
                border: inputs?.productId === value?.productId ? '2px solid #457672' : 'none',
              }}
              p={8}
              onClick={() => setInputs({productId: value?.productId})}
              mx={1}
            >
              <UiText fontWeight={600} fontSize={20}>
                {value?.label}
              </UiText>
              <UiText fontWeight={600} fontSize={22} my={4}>
                {value?.prefix}
                {value?.price}
                {value?.suffix}
              </UiText>
              <UiText fontWeight={600} fontSize={14} mb={4}>
                {value?.promoMessage}
              </UiText>
              <UiText fontSize={14} color="liteOuter">
                {value?.description}
              </UiText>
              <UiText fontWeight={600} fontSize={14} mt={6} mb={4}>
                {value?.typePrefix} {value?.type}
              </UiText>
              <UiText fontSize={12} color="liteOuter">
                {value?.howItWorks}
              </UiText>
              <UiDivider my={5} />
              <Row justify="center" w="100%">
                {value?.includes?.map(el => (
                  <Row
                    minH="40px"
                    key={el?.key}
                    mx={1}
                    style={{
                      border: '1px solid rgba(44, 65, 66, 0.1)',
                      borderRadius: '8px',
                    }}
                    textAlign="center"
                    align="center"
                    px={2}
                  >
                    <UiText fontSize={10} fontWeight={600}>
                      {el?.label}
                    </UiText>
                  </Row>
                ))}
              </Row>
            </Col>
          ))}
        </UiSimpleGrid>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire6Screen;
